import React, { useEffect, useState } from 'react';
import { Form, Switch, Checkbox } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Button } from 'components/Atom/Button';
import Select from 'components/Atom/Select';
import Input from 'components/Atom/Input';
import { useSelector } from 'react-redux';
import { addMeter, updateMeter } from 'api/subscriptions';
import { notification } from 'antd';
import './assets/style.css';

const aggregationOptions = [
  { label: 'Count', value: 'count' },
  { label: 'Sum', value: 'sum' },
  { label: 'Latest', value: 'latest' },
  { label: 'Max', value: 'max' },
  { label: 'Fixed Retainer', value: 'fixed-retainer' },
];

const subscriptionActionOptions = [
  { label: 'Cancel', value: 'cancel' },
  { label: 'Pause', value: 'pause' },
];

const planStatusOptions = [
  { label: 'Active', value: true },
  { label: 'Inactive', value: false },
];

const additionalTypeOptions = [
  { label: 'Fixed', value: 'fixed' },
  { label: 'Percentage', value: 'percentage' },
];

const normalizeNumber = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  return Number(value);
};

export const CreateMeter = ({ handleOpen, refetch, meterData, isEdit }) => {
  const [form] = Form.useForm();
  const [isMeterAdding, setIsMeterAdding] = useState(false);
  const store = useSelector((state) => state.company.store);
  const userStores = useSelector((state) => state?.company?.userStores);
  const isMobile = window.innerWidth < 768;
  const twoColumnGrid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 };

  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue({
        name: meterData?.name,
        active: meterData?.active ?? true,
      });
    } else {
      form.setFieldsValue({
        name: '',
        aggregation_type: undefined,
        aggregation_field: '',
        active: true,
        hasTaxCharges: false,
        taxes: [],
        subscriptionNextAction: undefined,
        planActive: undefined,
      });
    }
  }, [isEdit, meterData, form]);

  const onAddSubmit = async (values) => {
    if (userStores?.length > 1 && !store?.isSelected && !isEdit) {
      notification.error({ message: 'Select store from the drop-down menu above', placement: 'bottomRight' });
      return;
    }
    const { name, aggregation_type, aggregation_field, active, subscriptionNextAction, planActive, hasTaxCharges, taxes } = values;
    setIsMeterAdding(true);

    try {
      if (isEdit) {
        // For update, only include allowed fields
        const isAlreadyDeactivated = meterData?.active === false && active === false;

        const payload = {
          name,
        };

        // If meter is already deactivated and status hasn't changed, only send name
        if (!isAlreadyDeactivated) {
          payload.active = active;

          // Only include subscription next_action if active is false and action is selected
          if (!active && subscriptionNextAction) {
            payload.subscription = { next_action: subscriptionNextAction };
          }

          // Only include plan.active if active is false
          if (!active) {
            payload.plan = { active: planActive };
          }
        }

        const response = await updateMeter(meterData?.id, payload);
        if (response?.data?.success) {
          handleOpen();
          refetch();
          form.resetFields();
        }
      } else {
        const additionalArray = hasTaxCharges
          ? (taxes || [])
              ?.filter((item) => item?.key)
              ?.map((item) => ({
                key: item?.key?.trim(),
                value: normalizeNumber(item?.value),
                type: item?.type,
                inclusive: item?.inclusive === true,
              }))
          : [];

        const payload = {
          name,
          aggregation_type,
          aggregation_field,
          active,
          ...(hasTaxCharges && additionalArray?.length > 0 && { taxes: additionalArray }),
        };

        const response = await addMeter(store?._id, payload);
        if (response?.data?.success) {
          handleOpen();
          refetch();
          form.resetFields();
        }
      }
    } finally {
      setIsMeterAdding(false);
    }
  };

  const isActive = Form.useWatch('active', form);
  const hasTaxCharges = Form.useWatch('hasTaxCharges', form);

  const handleTaxChargesChange = (e) => {
    const checked = e?.target?.checked;
    if (checked) {
      const currentTaxes = form.getFieldValue('taxes') || [];
      form.setFieldsValue({
        hasTaxCharges: true,
        taxes: currentTaxes?.length > 0 ? currentTaxes : [{ key: undefined, value: undefined, type: undefined, inclusive: false }],
      });
    } else {
      form.setFieldsValue({ hasTaxCharges: false, taxes: [] });
    }
  };

  const handleRemoveTax = (remove, fieldName, fieldsLength) => {
    remove(fieldName);
    if (fieldsLength === 1) {
      form.setFieldsValue({ hasTaxCharges: false, taxes: [] });
    }
  };

  return (
    <div className='formDetails'>
      <Form form={form} layout='vertical' onFinish={onAddSubmit}>
        <div
          className='create-form'
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
          }}>
          <Form.Item
            name='name'
            label='Name'
            className='customInput mobileInputView steric-after'
            rules={[
              { required: true, message: 'Name is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const regex = /^[^%?{}[\]$^@<>"&!:;~*]+$/;
                  if (regex.test(value)) return Promise.resolve();
                  return Promise.reject('%?{}[]$^@<>"&!:;~* are not allowed.');
                },
              },
            ]}>
            <Input type='string' placeholder='Enter name' name='name' />
          </Form.Item>
          {!isEdit && (
            <>
              <Form.Item name='aggregation_type' label='Aggregation Type' className='customInput mobileInputView steric-after' rules={[{ required: true, message: 'Aggregation type is required' }]}>
                <Select showSearch defaultValue={'Select aggregation type'} optionsValue={aggregationOptions} labelInValue={false} />
              </Form.Item>
              <Form.Item
                name='aggregation_field'
                label='Aggregation Field'
                className='customInput mobileInputView steric-after'
                rules={[
                  { required: true, message: 'Aggregation field is required' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const regex = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
                      if (regex.test(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Aggregation field must be a valid JSON object key'));
                    },
                  },
                ]}>
                <Input type='string' placeholder='Enter aggregation field' name='aggregation_field' />
              </Form.Item>
            </>
          )}
          <Form.Item name='active' label='Active' valuePropName='checked' initialValue={true} className='customInput mobileInputView'>
            <Switch defaultChecked />
          </Form.Item>
          {!isEdit && (
            <Form.Item name='hasTaxCharges' valuePropName='checked' initialValue={false} className='customInput tax mobileInputView tax-charges-checkbox'>
              <Checkbox onChange={handleTaxChargesChange}>Additional Taxes</Checkbox>
            </Form.Item>
          )}
        </div>
        {!isEdit && hasTaxCharges && (
          <Form.List name='taxes'>
            {(fields, { add, remove }) => (
              <div className='tier-list'>
                {fields.map((field, index) => (
                  <div key={field.key} className='tier-item' style={{ marginBottom: 16 }}>
                    <div className='tier-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span className='tier-title'>Tax {index + 1}</span>
                      <DeleteOutlined onClick={() => handleRemoveTax(remove, field?.name, fields?.length)} style={{ color: 'red', cursor: 'pointer', fontSize: 18 }} />
                    </div>
                    <div className='create-form' style={twoColumnGrid}>
                      <Form.Item
                        name={[field.name, 'key']}
                        label='Key'
                        className='customInput mobileInputView steric-after'
                        validateFirst
                        rules={[
                          { required: true, message: 'Key is required' },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              const regex = /^[^%?{}[\]$^*@<>]*$/;
                              if (!regex.test(value)) {
                                return Promise.reject('Key contains invalid special characters');
                              }
                              const allAdditional = form.getFieldValue('taxes') || [];
                              const duplicate = allAdditional?.filter((item, idx) => item?.key === value && idx !== index);
                              if (duplicate?.length > 0) {
                                return Promise.reject('Key must be unique');
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}>
                        <Input type='string' placeholder='e.g. tax' />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'value']}
                        label='Value'
                        className='customInput mobileInputView steric-after'
                        validateFirst
                        rules={[
                          { required: true, message: 'Value is required' },
                          {
                            validator: (_, value) => {
                              if (value === undefined || value === null || value === '') return Promise.resolve();
                              if (Number(value) > 0) return Promise.resolve();
                              return Promise.reject('Value must be greater than 0');
                            },
                          },
                        ]}>
                        <Input type='number' placeholder='Enter value' />
                      </Form.Item>
                      <Form.Item name={[field.name, 'type']} label='Type' className='customInput mobileInputView steric-after' rules={[{ required: true, message: 'Type is required' }]}>
                        <Select optionsValue={additionalTypeOptions} labelInValue={false} placeholder='Select type' />
                      </Form.Item>
                      <Form.Item name={[field.name, 'inclusive']} label='Inclusive' valuePropName='checked' className='customInput mobileInputView checked-label' initialValue={false}>
                        <Switch />
                      </Form.Item>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button className='rounded-lg button-theme' style={{ width: 80 }} onClick={() => add({ key: undefined, value: undefined, type: undefined, inclusive: false })}>
                    + Tax
                  </Button>
                </div>
              </div>
            )}
          </Form.List>
        )}
        <div
          className='create-form'
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
          }}>
          {isEdit && isActive === false && meterData?.active !== false && (
            <>
              <Form.Item
                name='subscriptionNextAction'
                label='Associated Subscription action'
                className='customInput mobileInputView steric-after'
                rules={[{ required: true, message: 'Associated Subscription action is required' }]}>
                <Select optionsValue={subscriptionActionOptions} labelInValue={false} placeholder='Select action' />
              </Form.Item>
              <Form.Item
                name='planActive'
                label='Associated Plan status'
                className='customInput mobileInputView steric-after'
                rules={[{ required: true, message: 'Associated Plan status is required' }]}>
                <Select optionsValue={planStatusOptions} labelInValue={false} placeholder='Select status' />
              </Form.Item>
            </>
          )}
        </div>
        <div className='mb-[-20px] mt-[20px] form_button update-button'>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button block className='rounded-lg bg-plain' onClick={handleOpen}>
                Cancel
              </Button>
              <Button block className='button-theme ml-4 rounded-lg' htmlType='submit' loading={isMeterAdding}>
                {isEdit ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default CreateMeter;
